import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  Grid,
  IconButton,
  Alert,
  CircularProgress,
  Divider,
  alpha,
  Card,
  CardContent,
  Stack,
  Tooltip
} from '@mui/material';
import { format, addMonths, startOfMonth, endOfMonth, eachWeekOfInterval, addDays, isSameDay, isThursday } from 'date-fns';
import { Plus, Calendar, XCircle, Edit2, Trash2 } from 'lucide-react';
import axiosInstance from '../utils/axiosConfig';
import { Programming } from '../types/ConcertTypes';
import { DarkDate } from '../types/mongodb';
import DarkDateDialog from './DarkDateDialog';

interface ForecastViewProps {
  onAddProgramming: (date: Date) => void;
  onEditProgramming: (program: Programming) => void;
}

interface ThursdayEntry {
  date: Date;
  programming?: Programming;
  darkDate?: DarkDate;
  status: 'BOOKED' | 'PROVISIONAL' | 'CHECKING' | 'DARK' | 'EMPTY';
}

const STATUS_COLORS = {
  BOOKED: '#66bb6a',
  PROVISIONAL: '#ff7700',
  CHECKING: '#42a5f5',
  DARK: '#9e9e9e',
  EMPTY: '#ef5350'
} as const;

const ForecastView: React.FC<ForecastViewProps> = ({ onAddProgramming, onEditProgramming }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [thursdays, setThursdays] = useState<ThursdayEntry[]>([]);
  const [monthsToShow, setMonthsToShow] = useState(3);
  const [darkDateDialogOpen, setDarkDateDialogOpen] = useState(false);
  const [selectedDateForDark, setSelectedDateForDark] = useState<Date | null>(null);

  useEffect(() => {
    fetchForecastData();
  }, [monthsToShow]);

  const fetchForecastData = async () => {
    try {
      setLoading(true);
      setError(null);

      const today = new Date();
      const startDate = startOfMonth(today);
      const endDate = endOfMonth(addMonths(today, monthsToShow - 1));

      // Fetch programming and dark dates in parallel
      const [programmingResponse, darkDatesResponse] = await Promise.all([
        axiosInstance.get('/api/programmings/range', {
          params: {
            startDate: startDate.toISOString(),
            endDate: endDate.toISOString()
          }
        }),
        axiosInstance.get('/api/dark-dates/range', {
          params: {
            startDate: startDate.toISOString(),
            endDate: endDate.toISOString()
          }
        })
      ]);

      const programmings: Programming[] = programmingResponse.data;
      const darkDates: DarkDate[] = darkDatesResponse.data;

      // Generate all Thursdays in the range
      const allThursdays = generateThursdays(startDate, endDate);

      // Map each Thursday to its status
      const thursdayEntries: ThursdayEntry[] = allThursdays.map(thursday => {
        const programming = programmings.find(p => 
          isSameDay(new Date(p.date), thursday)
        );
        
        const darkDate = darkDates.find(d => 
          isSameDay(new Date(d.date), thursday)
        );

        let status: ThursdayEntry['status'];
        if (darkDate) {
          status = 'DARK';
        } else if (programming) {
          status = programming.status as 'BOOKED' | 'PROVISIONAL' | 'CHECKING';
        } else {
          status = 'EMPTY';
        }

        return {
          date: thursday,
          programming,
          darkDate,
          status
        };
      });

      setThursdays(thursdayEntries);
    } catch (err) {
      console.error('Failed to fetch forecast data:', err);
      setError('Failed to load forecast data');
    } finally {
      setLoading(false);
    }
  };

  const generateThursdays = (start: Date, end: Date): Date[] => {
    const thursdays: Date[] = [];
    const weeks = eachWeekOfInterval({ start, end });

    weeks.forEach(week => {
      const thursday = addDays(week, 4);
      if (thursday >= start && thursday <= end) {
        thursdays.push(thursday);
      }
    });

    return thursdays;
  };

  const handleMarkAsDark = (date: Date) => {
    setSelectedDateForDark(date);
    setDarkDateDialogOpen(true);
  };

  const handleDeleteDarkDate = async (darkDate: DarkDate) => {
    try {
      await axiosInstance.delete(`/api/dark-dates/${darkDate._id}`);
      fetchForecastData(); // Refresh
    } catch (err) {
      console.error('Failed to delete dark date:', err);
      setError('Failed to delete dark date');
    }
  };

  const getStatusChip = (status: ThursdayEntry['status']) => {
    return (
      <Chip
        label={status}
        size="small"
        sx={{
          bgcolor: STATUS_COLORS[status],
          color: status === 'PROVISIONAL' ? 'text.primary' : 'white',
          fontWeight: 'bold',
          minWidth: 100
        }}
      />
    );
  };

  const groupByMonth = () => {
    const grouped = new Map<string, ThursdayEntry[]>();
    
    thursdays.forEach(entry => {
      const monthKey = format(entry.date, 'MMMM yyyy');
      if (!grouped.has(monthKey)) {
        grouped.set(monthKey, []);
      }
      grouped.get(monthKey)!.push(entry);
    });

    return Array.from(grouped.entries());
  };

  const getStatusCounts = () => {
    const counts = {
      BOOKED: 0,
      PROVISIONAL: 0,
      CHECKING: 0,
      DARK: 0,
      EMPTY: 0
    };

    thursdays.forEach(entry => {
      counts[entry.status]++;
    });

    return counts;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const statusCounts = getStatusCounts();
  const monthGroups = groupByMonth();

  return (
    <Box>
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ bgcolor: alpha(STATUS_COLORS.BOOKED, 0.1) }}>
            <CardContent>
              <Typography variant="h4" sx={{ color: STATUS_COLORS.BOOKED }}>
                {statusCounts.BOOKED}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Booked
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ bgcolor: alpha(STATUS_COLORS.PROVISIONAL, 0.1) }}>
            <CardContent>
              <Typography variant="h4" sx={{ color: STATUS_COLORS.PROVISIONAL }}>
                {statusCounts.PROVISIONAL}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Provisional
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ bgcolor: alpha(STATUS_COLORS.CHECKING, 0.1) }}>
            <CardContent>
              <Typography variant="h4" sx={{ color: STATUS_COLORS.CHECKING }}>
                {statusCounts.CHECKING}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Checking
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ bgcolor: alpha(STATUS_COLORS.DARK, 0.1) }}>
            <CardContent>
              <Typography variant="h4" sx={{ color: STATUS_COLORS.DARK }}>
                {statusCounts.DARK}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Venue Closed
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ bgcolor: alpha(STATUS_COLORS.EMPTY, 0.1) }}>
            <CardContent>
              <Typography variant="h4" sx={{ color: STATUS_COLORS.EMPTY }}>
                {statusCounts.EMPTY}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Needs Decision
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Month selector */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3, gap: 1 }}>
        <Button
          variant={monthsToShow === 3 ? 'contained' : 'outlined'}
          onClick={() => setMonthsToShow(3)}
          size="small"
        >
          3 Months
        </Button>
        <Button
          variant={monthsToShow === 4 ? 'contained' : 'outlined'}
          onClick={() => setMonthsToShow(4)}
          size="small"
        >
          4 Months
        </Button>
        <Button
          variant={monthsToShow === 6 ? 'contained' : 'outlined'}
          onClick={() => setMonthsToShow(6)}
          size="small"
        >
          6 Months
        </Button>
            <Button
          variant={monthsToShow === 8 ? 'contained' : 'outlined'}
          onClick={() => setMonthsToShow(8)}
          size="small"
        >
          8 Months
        </Button>
              <Button
          variant={monthsToShow === 12 ? 'contained' : 'outlined'}
          onClick={() => setMonthsToShow(12)}
          size="small"
        >
          12 Months
        </Button>
      </Box>

      {/* Thursday listings by month */}
      {monthGroups.map(([monthKey, entries]) => (
        <Box key={monthKey} sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
            {monthKey}
          </Typography>
          
          <Stack spacing={2}>
            {entries.map((entry) => (
              <Paper
                key={entry.date.toISOString()}
                sx={{
                  p: 2,
                  bgcolor: alpha(STATUS_COLORS[entry.status], 0.05),
                  border: entry.status === 'EMPTY' ? `2px solid ${STATUS_COLORS.EMPTY}` : 'none'
                }}
              >
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={3}>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {format(entry.date, 'EEEE, MMM do')}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={2}>
                    {getStatusChip(entry.status)}
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    {entry.programming && (
                      <Box>
                        <Typography variant="body1" fontWeight="bold">
                          {entry.programming.artistName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {entry.programming.genre}
                        </Typography>
                      </Box>
                    )}
                    {entry.darkDate && (
                      <Box>
                        <Typography variant="body1" fontWeight="bold">
                          {entry.darkDate.reason}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {entry.darkDate.category}
                        </Typography>
                      </Box>
                    )}
                    {!entry.programming && !entry.darkDate && (
                      <Typography variant="body2" color="error">
                        No programming scheduled
                      </Typography>
                    )}
                  </Grid>

                  <Grid item xs={12} sm={3}>
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                      {entry.status === 'EMPTY' && (
                        <>
                          <Button
                            size="small"
                            variant="contained"
                            startIcon={<Plus size={16} />}
                            onClick={() => onAddProgramming(entry.date)}
                          >
                            Program
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<XCircle size={16} />}
                            onClick={() => handleMarkAsDark(entry.date)}
                          >
                            Mark Dark
                          </Button>
                        </>
                      )}
                      {entry.programming && entry.status !== 'BOOKED' && (
                        <IconButton
                          size="small"
                          onClick={() => onEditProgramming(entry.programming!)}
                        >
                          <Edit2 size={16} />
                        </IconButton>
                      )}
                      {entry.darkDate && (
                        <Tooltip title="Remove dark date">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteDarkDate(entry.darkDate!)}
                          >
                            <Trash2 size={16} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
            ))}
          </Stack>
        </Box>
      ))}

      <DarkDateDialog
        open={darkDateDialogOpen}
        date={selectedDateForDark}
        onClose={() => {
          setDarkDateDialogOpen(false);
          setSelectedDateForDark(null);
        }}
        onSuccess={() => {
          fetchForecastData();
        }}
      />
    </Box>
  );
};

export default ForecastView;